import { useObserver } from 'mobx-react-lite'
import React, {
  InputHTMLAttributes,
  memo,
  FocusEvent,
  MouseEvent,
  createRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  ChangeEvent,
  useMemo
} from 'react'
import { makeStyles } from '@material-ui/core'
import IASizedQuantity from './IASizedQuantity'
import classNames from 'classnames'
import { QuantityCellRef } from '../EnhancedCellTemplates/EnhancedQuantityCell/EnhancedQuantityCell'
import { map } from 'lodash'
import { ISize } from '@modules/order/stores/order/OrderDataStore'
import IADoorLevelTotalQuantity from './IADoorLevelTotalQuantity'
import IADoorLevelSizeQuantities from './IADoorLevelSizeQuantities'

interface IADoorLevelQuantityCustomEventwrapperProps {
  disabled: boolean
  isExclusionDoor: boolean
  isBooked: boolean
  highlightDoors: boolean
  doorProduct: any
  sizesList: any
  IADoorProduct: any
  showProductSizes: boolean
  hasCaptureSize: boolean
  showSizeQuantitySummary: boolean
  sizes: ISize[]
  autofocus?: boolean
  navigateToSize?: (index: number) => void
  navigateToCell?: (next: boolean) => void
  onSizeQuantityChange: (sizeId: string, e: ChangeEvent<HTMLInputElement>) => void
  onSizeQuantityKeyDown?: InputHTMLAttributes<HTMLInputElement>['onKeyDown']
  onSizeQuantityFocus?: (sizeId: string, e: FocusEvent<HTMLInputElement>) => void
  onSizeQuantityMouseDown?: (sizeId: string, e: MouseEvent) => void
  onDoorQuantityChangeHandler: InputHTMLAttributes<HTMLInputElement>['onChange']
  doorLevelQuantityKeyDown: InputHTMLAttributes<HTMLInputElement>['onKeyDown']
  doorLevelQuantityFocus: InputHTMLAttributes<HTMLInputElement>['onFocus']
  doorLevelQuantityMouseDown: InputHTMLAttributes<HTMLInputElement>['onMouseDown']
  onBlurHandler?: () => void
  isSizeQtyUpdated?: boolean
}

const useStyles = makeStyles(theme => ({
  wrapperRoot: {
    width: '100%'
  },
  sizeQuantitiesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    height: 'max-content',
    width: '100%'
  },
  sizeInput: {
    width: 60,
    height: 20,
    marginTop: 0
  },
  inactiveSize: {
    opacity: 0.5
  },
  totalSizes: {
    height: 90
  },
  lesserHeightSize: {
    height: 80
  }
}))

/**
 * Main door cell in IA view
 */
const IADoorLevelQuantityCustomEventwrapper = forwardRef<
  QuantityCellRef,
  IADoorLevelQuantityCustomEventwrapperProps
>(
  (
    {
      disabled,
      isExclusionDoor,
      isBooked,
      doorProduct,
      sizesList,
      IADoorProduct,
      showProductSizes,
      hasCaptureSize,
      autofocus,
      highlightDoors,
      isSizeQtyUpdated,
      onSizeQuantityChange,
      navigateToSize,
      onSizeQuantityKeyDown,
      navigateToCell,
      onSizeQuantityFocus,
      onDoorQuantityChangeHandler,
      onSizeQuantityMouseDown,
      doorLevelQuantityKeyDown,
      onBlurHandler,
      doorLevelQuantityFocus,
      doorLevelQuantityMouseDown
    },
    ref
  ) =>
    useObserver(function useHook() {
      const classes = useStyles()
      const doorLevelQuantityInputRef = useRef<HTMLInputElement>()

      const keyDownHandler = useCallback((e, currSizeIndex) => {
        if (e.which === 9) {
          if (e.shiftKey) {
            navigateToSize(currSizeIndex - 1)
          } else {
            navigateToSize(currSizeIndex + 1)
          }
          e.preventDefault()
        }
        onSizeQuantityKeyDown?.(e)
      }, [])

      const lastSizeKeyDownHandler = useCallback(
        (e, availableSizes) => {
          if (e.which === 9) {
            if (e.shiftKey) {
              navigateToSize(availableSizes.length - 1)
              ;(sizesEle[availableSizes.length - 1] as any)?.ref.current?.focus()
            } else {
              navigateToCell(true)
            }
            e.preventDefault()
          }
          onSizeQuantityKeyDown(e)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
      )

      const sizesEle: any = useMemo(() => {
        if (sizesList?.length) {
          const availableSizes = [...sizesList]
          const availableSizesRef = availableSizes.map(() =>
            createRef<HTMLInputElement>()
          )
          const lastSize = availableSizes.pop()
          const lastSizeRef = availableSizesRef.pop()
          return availableSizes
            ?.map(({ id: sizeId, isInactive = false, ...rest }, currSizeIndex) => {
              return (
                <IASizedQuantity
                  mainOrderProductSizeValue={
                    doorProduct?.doorOrderSizeQuantities?.[sizeId]?.quantity || 0
                  }
                  key={sizeId}
                  ref={availableSizesRef[currSizeIndex]}
                  onChange={e => {
                    onSizeQuantityChange(sizeId, e)
                  }}
                  value={IADoorProduct?.deliverySizeQuantities?.[sizeId]?.quantity || 0}
                  disabled={disabled}
                  className={classNames(classes.sizeInput, {
                    [classes.inactiveSize]: isInactive
                  })}
                  onKeyDown={e => {
                    keyDownHandler(e, currSizeIndex)
                  }}
                  onFocus={e => {
                    navigateToSize(currSizeIndex)
                    onSizeQuantityFocus?.(sizeId, e)
                  }}
                  onMouseDown={e => {
                    navigateToSize(currSizeIndex)
                    onSizeQuantityMouseDown?.(sizeId, e)
                  }}
                  onBlur={onBlurHandler}
                />
              )
            })
            .concat([
              <IASizedQuantity
                mainOrderProductSizeValue={
                  doorProduct?.doorOrderSizeQuantities?.[lastSize.id]?.quantity || 0
                }
                key={lastSize.id}
                ref={lastSizeRef}
                onChange={e => {
                  onSizeQuantityChange(lastSize.id, e)
                }}
                value={
                  IADoorProduct?.deliverySizeQuantities?.[lastSize.id]?.quantity || 0
                }
                disabled={disabled}
                className={classNames(classes.sizeInput, {
                  [classes.inactiveSize]: lastSize.isInactive
                })}
                onKeyDown={e => {
                  lastSizeKeyDownHandler(e, availableSizes)
                }}
                onFocus={e => {
                  navigateToSize(availableSizes.length)
                  onSizeQuantityFocus?.(lastSize.id, e)
                }}
                onMouseDown={e => {
                  navigateToSize(availableSizes.length)
                  onSizeQuantityMouseDown?.(lastSize.id, e)
                }}
                onBlur={onBlurHandler}
              />
            ])
        }
        return []
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [sizesList, doorProduct, IADoorProduct, isSizeQtyUpdated])

      useImperativeHandle(
        ref,
        () => ({
          quantityInputRef: doorLevelQuantityInputRef.current,
          sizeElementRefs: map(sizesEle, 'ref.current'),
          focusQtyInput: () => {
            doorLevelQuantityInputRef?.current?.focus()
          },
          focusSizeInput: index => {
            sizesEle[index]?.ref.current?.focus()
          }
        }),
        [sizesEle]
      )

      const doorQtyKeyDownHandler = useCallback(
        e => {
          if (e.which === 9) {
            if (e.shiftKey) {
              navigateToCell(false)
            } else {
              if (sizesEle?.length) {
                navigateToSize(0)
              } else {
                navigateToCell(true)
              }
            }
            e.preventDefault()
          }
          doorLevelQuantityKeyDown?.(e)
        },
        [sizesEle]
      )

      return (
        <div className={classes.wrapperRoot}>
          <IADoorLevelTotalQuantity
            doorProduct={doorProduct}
            IADoorProduct={IADoorProduct}
            isBooked={isBooked}
            disabled={disabled}
            isExclusionDoor={isExclusionDoor}
            highlightDoors={highlightDoors}
            ref={doorLevelQuantityInputRef}
            onChange={onDoorQuantityChangeHandler}
            onKeyDown={doorQtyKeyDownHandler}
            autoFocus={autofocus}
            onFocus={doorLevelQuantityFocus}
            onMouseDown={doorLevelQuantityMouseDown}
            onBlur={onBlurHandler}
          />
          <IADoorLevelSizeQuantities
            doorProduct={doorProduct}
            IADoorProduct={IADoorProduct}
            showProductSizes={showProductSizes}
            hasCaptureSize={hasCaptureSize}
            sizesElement={sizesEle}
            disabled={disabled}
          />
        </div>
      )
    })
)

export default memo(IADoorLevelQuantityCustomEventwrapper)
